import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, Lock, Circle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pillars, setPillars] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/course/progress"),
      api.get("/course/overview"),
    ]).then(([p, o]) => {
      setData(p.data);
      setPillars(o.data.pillars);
    });
  }, []);

  if (!data) return <div className="py-32 text-center text-[#8A857D]">Loading...</div>;

  const totalCompleted = data.pillars.reduce((s, p) => s + p.completed, 0);
  const totalLessons = data.pillars.reduce((s, p) => s + p.total, 0);
  const allComplete = totalLessons > 0 && totalCompleted >= totalLessons;

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E]">Welcome, {user?.name}</div>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2D2A26] mt-3 tracking-tight">Your journey</h1>

      {/* Certificate banner */}
      {allComplete && data.certificate_token && (
        <div className="mt-10 bg-gradient-to-r from-[#A36B5E] to-[#8C5A4E] text-white rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="dashboard-certificate-banner">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/70">All nine lessons complete</div>
            <div className="font-display text-3xl mt-2">Your certificate is ready.</div>
            <p className="text-white/85 mt-2 max-w-xl">A small marker of a real change. Download it, share it, frame it.</p>
          </div>
          <button
            onClick={() => nav(`/certificate/${data.certificate_token}`)}
            data-testid="dashboard-certificate-cta"
            className="bg-white text-[#A36B5E] rounded-full px-7 py-3.5 font-medium hover:bg-[#FDFBF7] transition-colors whitespace-nowrap"
          >
            View certificate →
          </button>
        </div>
      )}

      {/* Status */}
      <div className="mt-10 grid sm:grid-cols-3 gap-6">
        <div className="bg-white border border-[#EAE5D9] rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-[#8A857D]">Alignment Stage</div>
          <div className="font-display text-3xl text-[#A36B5E] italic mt-2" data-testid="dashboard-stage">
            {user?.alignment_stage || "Not yet measured"}
          </div>
          {!user?.alignment_stage && (
            <Link to="/assessment" className="text-sm text-[#A36B5E] mt-2 inline-block">Take the assessment →</Link>
          )}
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-[#8A857D]">Lessons Completed</div>
          <div className="font-display text-3xl text-[#2D2A26] mt-2" data-testid="dashboard-progress">
            {totalCompleted} / {totalLessons}
          </div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-[#8A857D]">Access</div>
          <div className="font-display text-2xl text-[#2D2A26] mt-2">
            {data.has_paid ? "Full Access" : "Free Preview"}
          </div>
          {!data.has_paid && (
            <button
              onClick={() => nav("/pricing")}
              data-testid="dashboard-unlock"
              className="text-sm text-[#A36B5E] mt-2"
            >
              Unlock the full course →
            </button>
          )}
        </div>
      </div>

      {/* Pillars */}
      <div className="mt-16 space-y-10">
        {pillars.map((p) => {
          const summary = data.pillars.find((s) => s.pillar_id === p.id) || { completed: 0, total: p.lessons.length };
          return (
            <div key={p.id} className="bg-white border border-[#EAE5D9] rounded-2xl p-8" data-testid={`dashboard-pillar-${p.id}`}>
              <div className="flex flex-wrap justify-between items-baseline gap-4 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#A36B5E]">{p.scripture_ref}</div>
                  <h2 className="font-display text-2xl sm:text-3xl text-[#2D2A26] mt-1">{p.title}</h2>
                </div>
                <div className="text-sm text-[#8A857D]">
                  {summary.completed} of {summary.total} complete
                </div>
              </div>
              <div className="h-1 bg-[#F5F2EB] rounded-full overflow-hidden mb-6">
                <div
                  className="h-full bg-[#5F6F52] transition-all duration-500"
                  style={{ width: `${(summary.completed / summary.total) * 100}%` }}
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {p.lessons.map((l, i) => {
                  const isPreview = p.id === pillars[0].id && i === 0;
                  const isCompleted = (data.progress?.[p.id] || []).includes(l.id);
                  const isLocked = !data.has_paid && !isPreview;
                  return (
                    <button
                      key={l.id}
                      onClick={() => !isLocked && nav(`/lesson/${p.id}/${l.id}`)}
                      data-testid={`dashboard-lesson-${p.id}-${l.id}`}
                      className={`text-left px-5 py-4 rounded-xl border transition-all ${
                        isLocked
                          ? "border-[#EAE5D9] bg-[#F5F2EB]/50 opacity-60 cursor-not-allowed"
                          : "border-[#EAE5D9] hover:border-[#A36B5E] hover:-translate-y-0.5"
                      }`}
                      disabled={isLocked}
                    >
                      <div className="flex items-start gap-3">
                        {isLocked ? <Lock size={16} className="text-[#8A857D] mt-1" /> :
                          isCompleted ? <CheckCircle2 size={16} className="text-[#5F6F52] mt-1" /> :
                          <Circle size={16} className="text-[#8A857D] mt-1" />}
                        <div className="flex-1">
                          <div className="text-xs uppercase tracking-widest text-[#8A857D]">Lesson {i + 1}</div>
                          <div className="text-[#2D2A26] mt-1">{l.title}</div>
                          <div className="text-xs text-[#8A857D] mt-1">{l.duration_min} min</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
